import { useEffect, useState, type FormEvent } from "react";
import { ApiError, get, patch } from "../api/client";
import type { HandleCheck, User } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { ErrorLine, Sheet } from "./ui";

export const HANDLE_RE = /^[a-z][a-z0-9_]{2,23}$/;

export function cleanHandleInput(raw: string): string {
  return raw.trim().replace(/^@+/, "").toLowerCase();
}

export function initialsOf(displayName?: string | null, handle?: string | null): string {
  const s = (displayName || handle || "").replace(/^@+/, "").trim();
  if (!s) return "DT";
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

export function HandleSheet({
  initialHandle,
  title = "Your @handle",
  sub = "Managers assign your batting and bowling clips to your @handle. Your clips live at deepthird.com/u/@handle.",
  cancelLabel,
  onClose,
  onSaved,
}: {
  initialHandle?: string | null | undefined;
  title?: string | undefined;
  sub?: string | undefined;
  cancelLabel?: string | undefined;
  onClose: () => void;
  onSaved?: ((handle: string) => void) | undefined;
}) {
  const { me, refresh } = useAuth();
  const current = initialHandle ?? me?.user.handle ?? "";
  const [value, setValue] = useState(current);
  const [check, setCheck] = useState<HandleCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);

  const clean = cleanHandleInput(value);
  const unchanged = clean !== "" && clean === current;

  useEffect(() => {
    setErr(null);
    if (!clean) {
      setCheck(null);
      setChecking(false);
      return;
    }
    if (unchanged && HANDLE_RE.test(clean)) {
      setCheck({ handle: clean, valid: true, available: true, problem: null, message: null, suggestions: [] });
      setChecking(false);
      return;
    }
    if (!HANDLE_RE.test(clean)) {
      setCheck({
        handle: clean,
        valid: false,
        available: false,
        problem: "format",
        message: "3 to 24 lowercase letters, digits, or _",
        suggestions: [],
      });
      setChecking(false);
      return;
    }
    let cancelled = false;
    setChecking(true);
    const t = window.setTimeout(() => {
      get<HandleCheck>(`/v1/users/check-handle?handle=${encodeURIComponent(clean)}`)
        .then((res) => {
          if (!cancelled) setCheck(res);
        })
        .catch(() => {
          if (!cancelled) setCheck(null);
        })
        .finally(() => {
          if (!cancelled) setChecking(false);
        });
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [clean, unchanged]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !clean) return;
    if (unchanged) {
      onSaved?.(clean);
      onClose();
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await patch<{ user: User }>("/v1/me", { handle: clean });
      await refresh();
      onSaved?.(res.user.handle ?? clean);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const sugs = Array.isArray(e.details?.suggestions) ? (e.details.suggestions as string[]) : [];
        setCheck({
          handle: clean,
          valid: true,
          available: false,
          problem: "taken",
          message: e.message,
          suggestions: sugs,
        });
      } else {
        setErr(e as Error);
      }
    } finally {
      setBusy(false);
    }
  };

  const invalid = check !== null && (!check.valid || !check.available);
  const canSave = !busy && !checking && clean.length >= 3 && (unchanged || (check?.valid && check?.available));

  return (
    <Sheet onClose={() => !busy && onClose()}>
      <form onSubmit={submit} data-testid="handle-sheet">
        <h2>{title}</h2>
        <p className="sub mt6">{sub}</p>
        <label className="tiny mt16" style={{ display: "block" }} htmlFor="handle-input">Handle</label>
        <div className="handle-field mt6">
          <span className="handle-at">@</span>
          <input
            id="handle-input"
            className={`field handle-in ${invalid ? "invalid" : "focus"}`}
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/^@+/, "").toLowerCase())}
            placeholder="rohan"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={24}
            data-testid="handle-input"
          />
        </div>
        {checking ? (
          <p className="tiny mt6">Checking @{clean}…</p>
        ) : check ? (
          check.valid && check.available ? (
            <p className="tiny mt6" style={{ color: "var(--ok)" }} data-testid="handle-available">
              Available · 3 to 24 lowercase letters, digits, or _
            </p>
          ) : (
            <p className="tiny bad mt6" data-testid="handle-problem">
              {check.message || `@${clean} is already taken.`}
              {check.suggestions.length > 0 ? " Try one of these:" : ""}
            </p>
          )
        ) : (
          <p className="tiny mt6">3 to 24 lowercase letters, digits, or _</p>
        )}
        {check && check.suggestions.length > 0 && (
          <div className="handle-sugs" data-testid="handle-suggestions">
            {check.suggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="handle-sug"
                onClick={() => setValue(s)}
                data-testid={`handle-sug-${s}`}
              >
                @{s}
              </button>
            ))}
          </div>
        )}
        <ErrorLine error={err} />
        <div className="mt20">
          <button type="submit" className="btn dark full" disabled={!canSave} data-testid="handle-save">
            {busy ? "Saving…" : "Save handle"}
          </button>
        </div>
        <div className="mt10">
          <button type="button" className="btn ghost full" onClick={onClose} disabled={busy} data-testid="handle-cancel">
            {cancelLabel ?? (current ? `Keep @${current}` : "Cancel")}
          </button>
        </div>
      </form>
    </Sheet>
  );
}
